import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, X, Package, AlertTriangle } from "lucide-react";

const empty = { name: "", price: "", category: "", stock: "0", track_stock: true, low_stock_threshold: "5" };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get(`/products?search=${encodeURIComponent(search)}`).then((r) => setProducts(r.data));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name, category: form.category, track_stock: form.track_stock,
        price: parseFloat(form.price), stock: parseInt(form.stock || 0, 10),
        low_stock_threshold: parseInt(form.low_stock_threshold || 0, 10),
      };
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Producto guardado"); setForm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (p) => { if (window.confirm(`¿Eliminar ${p.name}?`)) { await api.delete(`/products/${p.id}`); load(); } };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div><p className="overline text-[#00f0ff]">Inventario</p><h1 className="font-display text-5xl">PRODUCTOS</h1></div>
        <button data-testid="product-add-button" onClick={() => setForm({ ...empty })} className="flex items-center gap-2 bg-[#00f0ff] text-black font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#00c2cc]">
          <Plus className="w-4 h-4" /> Nuevo producto
        </button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input data-testid="product-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar producto..." className="w-full bg-[#121212] border border-white/10 pl-10 pr-4 py-2.5 text-sm focus:border-[#00f0ff] focus:outline-none" />
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="products-table">
          <thead><tr className="text-left overline border-b border-white/10">
            <th className="px-4 py-4">Producto</th><th className="px-4 py-4">Categoría</th>
            <th className="px-4 py-4 text-right">Precio</th><th className="px-4 py-4 text-right">Stock</th><th className="px-4 py-4 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {products.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-white/30">Sin productos.</td></tr>}
            {products.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#1a1a1a] border border-white/10 flex items-center justify-center"><Package className="w-4 h-4 text-white/40" /></div>
                    <span className="font-semibold">{p.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-white/60">{p.category || "—"}</td>
                <td className="px-4 py-3 text-right font-display text-2xl text-[#39FF14]">${p.price?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">
                  {p.track_stock ? (
                    <span className={`inline-flex items-center gap-1.5 font-mono-logs ${p.low_stock ? "text-[#FFD60A]" : "text-white"}`}>
                      {p.low_stock && <AlertTriangle className="w-3.5 h-3.5" />}{p.stock}
                    </span>
                  ) : <span className="text-white/30 text-xs">Sin control</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`product-edit-${p.id}`} onClick={() => setForm({ ...p, price: String(p.price), stock: String(p.stock), low_stock_threshold: String(p.low_stock_threshold) })} className="p-2 hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`product-delete-${p.id}`} onClick={() => del(p)} className="p-2 hover:bg-white/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto">
          <form onSubmit={save} data-testid="product-form" className="bg-[#0a0a0a] border border-white/20 w-full max-w-md my-12">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="font-display text-2xl">{form.id ? "EDITAR PRODUCTO" : "NUEVO PRODUCTO"}</h3>
              <button type="button" onClick={() => setForm(null)}><X className="w-5 h-5 text-white/50" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="overline block mb-1.5">Nombre *</label>
                <input data-testid="product-field-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="overline block mb-1.5">Precio *</label>
                  <input data-testid="product-field-price" required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
                <div><label className="overline block mb-1.5">Categoría</label>
                  <input data-testid="product-field-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="product-field-track" type="checkbox" checked={form.track_stock} onChange={(e) => setForm({ ...form, track_stock: e.target.checked })} className="accent-[#00f0ff] w-4 h-4" />
                Controlar inventario (stock)
              </label>
              {form.track_stock && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="overline block mb-1.5">Stock</label>
                    <input data-testid="product-field-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
                  <div><label className="overline block mb-1.5">Alerta bajo stock</label>
                    <input data-testid="product-field-threshold" type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/10">
              <button type="button" onClick={() => setForm(null)} className="border border-white/20 px-5 py-2.5 text-sm hover:bg-white/5">Cancelar</button>
              <button type="submit" data-testid="product-save-button" className="bg-[#00f0ff] text-black font-bold uppercase text-sm px-6 py-2.5 hover:bg-[#00c2cc]">Guardar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
